import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Package, FileText, Layers } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useSkins, Skin } from "@/hooks/useSkins";

// Static pages data
const staticPages = [
  { name: "Køb Skins", description: "Udforsk vores CS2 skin katalog", path: "/koeb-skins", type: "page" as const },
  { name: "Sælg Skins", description: "Sælg dine CS2 skins til os", path: "/saelg-skins", type: "page" as const },
  { name: "Blogs", description: "Læs vores artikler om CS2 skins", path: "/blogs", type: "page" as const },
  { name: "FAQ", description: "Ofte stillede spørgsmål", path: "/faq", type: "page" as const },
  { name: "Kontakt Os", description: "Kontakt vores kundeservice", path: "/kontakt-os", type: "page" as const },
  { name: "Om Os", description: "Lær mere om RamborghInvest", path: "/#about", type: "page" as const },
  { name: "Privatlivspolitik", description: "Vores privatlivspolitik", path: "/privatlivspolitik", type: "page" as const },
  { name: "Handelsbetingelser", description: "Vores handelsbetingelser", path: "/handelsbetingelser", type: "page" as const },
];

// Blog posts data
const blogPosts = [
  { name: "Sælg dine CS2 skins sikkert", description: "Lær hvordan du sælger dine CS2 skins på en sikker måde", path: "/blog/saelg-skins-sikkert", type: "blog" as const },
  { name: "Hvad er dit CS2 skin værd?", description: "Find ud af hvordan du vurderer værdien af dine CS2 skins", path: "/blog/skin-vaerdi", type: "blog" as const },
  { name: "Hvorfor vælge en dansk skin shop?", description: "Fordelene ved at handle CS2 skins hos en dansk forhandler", path: "/blog/dansk-cs2-shop", type: "blog" as const },
  { name: "Sådan undgår du scams i CS2 handel", description: "Tips til at beskytte dig selv mod svindlere", path: "/blog/undgaa-scams", type: "blog" as const },
];

type SearchResult = {
  name: string;
  description: string;
  path: string;
  type: "skin" | "blog" | "page";
  image?: string;
  price?: number;
};

interface SearchAutocompleteProps {
  className?: string;
  onClose?: () => void;
  autoFocus?: boolean;
}

const SearchAutocomplete = ({ className = "", onClose, autoFocus = false }: SearchAutocompleteProps) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: skins } = useSkins();

  // Search logic
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const searchTerm = query.toLowerCase().trim();
    const searchResults: SearchResult[] = [];

    // Search skins
    if (skins) {
      const matchedSkins = skins
        .filter(
          (skin) =>
            skin.name.toLowerCase().includes(searchTerm) ||
            skin.description?.toLowerCase().includes(searchTerm) ||
            skin.category?.toLowerCase().includes(searchTerm) ||
            skin.weapon_type?.toLowerCase().includes(searchTerm)
        )
        .slice(0, 5)
        .map((skin) => ({
          name: skin.name,
          description: `${skin.condition} • ${skin.price.toLocaleString("da-DK")} DKK`,
          path: `/koeb-skins?skin=${encodeURIComponent(skin.name)}`,
          type: "skin" as const,
          image: skin.preview_image,
          price: skin.price,
        }));
      searchResults.push(...matchedSkins);
    }

    // Search blog posts
    const matchedBlogs = blogPosts
      .filter(
        (blog) =>
          blog.name.toLowerCase().includes(searchTerm) ||
          blog.description.toLowerCase().includes(searchTerm)
      )
      .slice(0, 3)
      .map((blog) => ({
        ...blog,
        type: "blog" as const,
      }));
    searchResults.push(...matchedBlogs);

    // Search static pages
    const matchedPages = staticPages
      .filter(
        (page) =>
          page.name.toLowerCase().includes(searchTerm) ||
          page.description.toLowerCase().includes(searchTerm)
      )
      .slice(0, 3)
      .map((page) => ({
        ...page,
        type: "page" as const,
      }));
    searchResults.push(...matchedPages);

    setResults(searchResults);
    setIsOpen(searchResults.length > 0);
    setSelectedIndex(-1);
  }, [query, skins]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (result: SearchResult) => {
    setIsOpen(false);
    setQuery("");
    
    // Smooth transition with slight delay for dropdown to close
    requestAnimationFrame(() => {
      navigate(result.path);
      // Smooth scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    
    onClose?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && results[selectedIndex]) {
        handleSelect(results[selectedIndex]);
      } else if (query.trim()) {
        // Default search behavior with smooth transition
        setIsOpen(false);
        setQuery("");
        requestAnimationFrame(() => {
          navigate(`/koeb-skins?search=${encodeURIComponent(query)}`);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
        onClose?.();
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const getTypeIcon = (type: "skin" | "blog" | "page") => {
    switch (type) {
      case "skin":
        return <Package className="w-4 h-4 text-gray-400" />;
      case "blog":
        return <FileText className="w-4 h-4 text-gray-400" />;
      case "page":
        return <Layers className="w-4 h-4 text-gray-400" />;
    }
  };

  const getTypeLabel = (type: "skin" | "blog" | "page") => {
    switch (type) {
      case "skin":
        return "Skin";
      case "blog":
        return "Blog";
      case "page":
        return "Side";
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Søg efter skins, sider..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim() && results.length > 0 && setIsOpen(true)}
          autoFocus={autoFocus}
          className="w-full pl-10 pr-4 py-2 bg-white border border-black text-black placeholder:text-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-black"
        />
      </div>

      {/* Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-black rounded-lg shadow-xl z-[100] max-h-[400px] overflow-y-auto">
          {/* Group by type */}
          {["skin", "blog", "page"].map((type) => {
            const typeResults = results.filter((r) => r.type === type);
            if (typeResults.length === 0) return null;

            return (
              <div key={type}>
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase bg-gray-50 border-b border-gray-200">
                  {type === "skin" ? "Skins" : type === "blog" ? "Blogindlæg" : "Sider"}
                </div>
                {typeResults.map((result, idx) => {
                  const globalIndex = results.indexOf(result);
                  return (
                    <button
                      key={`${result.type}-${idx}`}
                      onClick={() => handleSelect(result)}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                        selectedIndex === globalIndex
                          ? "bg-gray-100"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      {/* Image or Icon */}
                      {result.image ? (
                        <img
                          src={result.image}
                          alt={result.name}
                          className="w-10 h-10 object-contain bg-gray-100 rounded"
                        />
                      ) : (
                        <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded">
                          {getTypeIcon(result.type)}
                        </div>
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-black text-sm truncate">
                          {result.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {result.description}
                        </div>
                      </div>

                      {/* Type badge */}
                      <span className="text-[10px] font-medium text-gray-400 uppercase px-2 py-1 bg-gray-100 rounded">
                        {getTypeLabel(result.type)}
                      </span>
                    </button>
                  );
                })}
              </div>
            );
          })}

          {/* Search all */}
          {query.trim() && (
            <button
              onClick={() => {
                setIsOpen(false);
                setQuery("");
                requestAnimationFrame(() => {
                  navigate(`/koeb-skins?search=${encodeURIComponent(query)}`);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                });
                onClose?.();
              }}
              className="w-full flex items-center gap-3 px-3 py-3 text-left border-t border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded">
                <Search className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex-1">
                <span className="text-sm text-black">
                  Søg efter "<span className="font-medium">{query}</span>" i shoppen
                </span>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchAutocomplete;
